import { useMemo, useState } from 'react'
import { ArrowLeftIcon, LockSimpleIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { Crown } from 'iconoir-react'
import { Button } from 'app/components/ui/button'
import { Checkbox } from 'app/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { Switch } from 'app/components/ui/switch'
import type { ListCopy } from 'lib/listCopy'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import { templateLock } from 'app/lib/workflows/blockGates'
import type { BlockedGate, GateLocation, GateToggles } from 'app/types/workflow-gates'
import { workflowTemplates, type WorkflowTemplate } from 'app/lib/workflows/workflowTemplates'
import { DeployTemplatesFooter } from './DeployTemplatesFooter'
import { LockPanel } from './LockPanel'

const BADGE_STYLES: Record<WorkflowTemplate[`badge`], string> = {
  'Speed to lead': `bg-blue-50 text-blue-700 ring-blue-200`,
  'PMS-powered': `bg-emerald-50 text-emerald-700 ring-emerald-200`,
  'Win-back': `bg-violet-50 text-violet-700 ring-violet-200`,
  'Show rate': `bg-amber-50 text-amber-700 ring-amber-200`,
  'Case acceptance': `bg-rose-50 text-rose-700 ring-rose-200`,
  'Call conversion': `bg-teal-50 text-teal-700 ring-teal-200`,
  // Amber, the same register as the crown, so a system card reads as ours at a glance rather than
  // as one more funnel colour.
  'Action Center': `bg-amber-50 text-amber-800 ring-amber-200`,
}

/** The category filter chips: round, small, pressed state from the toggle variant. */
const CHIP_CLASS = `h-6 rounded-full px-2.5 text-[11px]`

/** Fixed order so the gallery reads as a patient journey: win them, keep them, protect the booking,
 *  then follow the visit. Categories with no matches drop out of the list rather than sit empty. */
const CATEGORY_ORDER: WorkflowTemplate[`category`][] = [
  `New patient enquiries`,
  `Recall and reactivation`,
  `Appointments`,
  `After the visit`,
  `PMS sync`,
]

/** The Action Center's own, which live behind their own screen rather than in the gallery. They are
 *  ours and a practice can never start one, so mixing them into the categories put a group nobody
 *  else has in the middle of a page that is otherwise the same for everyone.
 *
 *  Split in one place, not per filter, so a system template cannot reach the practice's search, chip
 *  counts or "All" total by being filtered a step too late. */
const splitTemplates = (copy: ListCopy) => {
  const all = workflowTemplates(copy)
  return {
    system: all.filter((template) => template.category === `System`),
    gallery: all.filter((template) => template.category !== `System`),
  }
}

/** What a search looks at. Deliberately includes the badge and category, so typing "recall",
 *  "no show" or "speed to lead" all find something, and the gated note, so someone hunting for the
 *  thing they were promised can find it even while it waits on the backend. */
const haystack = (template: WorkflowTemplate) =>
  [template.title, template.description, template.badge, template.category, template.gatedNote ?? ``]
    .join(` `)
    .toLowerCase()

/** One template. Shared by the gallery and the system screen so a card cannot come to mean two
 *  different things depending on which screen drew it.
 *
 *  `onToggleSelect` puts the card in deploy mode: it shows a tick box and reads as selected. The
 *  tick box sits beside the card rather than inside it, because a control nested in the card's own
 *  button would hand its click to `onPick`. */
function TemplateCard({
  template,
  onPick,
  selected,
  onToggleSelect,
  lock,
  lockLocation,
}: {
  template: WorkflowTemplate
  onPick: () => void
  selected?: boolean
  onToggleSelect?: () => void
  /** No location in the workspace can run it: the card opens why instead of the builder. */
  lock?: Extract<BlockedGate, { state: `locked` }>
  lockLocation?: GateLocation
}) {
  const ready = template.status === `ready`
  const card = (
    <Button
      variant="card"
      disabled={!ready}
      title={ready ? template.description : `${template.description}\n\n${template.gatedNote}`}
      onClick={lock ? undefined : () => ready && onPick()}
      data-testid={lock ? `template-locked-${template.id}` : undefined}
      className={cn(
        `h-full flex-col items-start`,
        ready
          ? `hover:border-strong hover:bg-surface hover:shadow-sm`
          : `opacity-60 disabled:border-default disabled:bg-surface`,
        selected && `border-amber-400 ring-1 ring-amber-200 hover:border-amber-400`,
      )}
    >
      <div className="flex w-full items-center justify-between gap-1">
        <span
          className={cn(`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ring-1`, BADGE_STYLES[template.badge])}
        >
          {template.badge}
        </span>
        {!ready && (
          <span className="flex items-center gap-1 text-[9px] font-medium text-minimal">
            <LockSimpleIcon size={10} weight="bold" />
            Coming soon
          </span>
        )}
        {ready && lock && <LockSimpleIcon size={12} weight="bold" className="shrink-0 text-subtle" />}
        {/* The same crown the workflows list puts on the rows these produce, so the template and the
              thing it makes are recognisably one and the same. Shifts left to leave the corner to the
              tick box when there is one. */}
        {template.category === `System` && (
          <Crown width={14} height={14} className={cn(`shrink-0 text-amber-800`, onToggleSelect && `mr-6`)} />
        )}
      </div>
      <span className="text-xs font-semibold text-default">{template.title}</span>
      <span className="line-clamp-3 text-[11px] leading-snug text-subtle">{template.description}</span>
      {/* Clamped, with the full reason still on hover. These notes run to a paragraph because they
            carry real evidence, and unclamped they made one card four times the height of its
            neighbour. */}
      {!ready && template.gatedNote && (
        <span className="line-clamp-2 text-[10px] leading-snug text-minimal">{template.gatedNote}</span>
      )}
    </Button>
  )
  return (
    <div className="relative h-full">
      {lock ? (
        <Popover>
          <PopoverTrigger asChild>{card}</PopoverTrigger>
          <PopoverContent align="start" className="w-64">
            <LockPanel
              gate={lock}
              blockLabel={template.title}
              blockKind={`template-${template.id}`}
              location={lockLocation}
            />
          </PopoverContent>
        </Popover>
      ) : (
        card
      )}
      {onToggleSelect && ready && (
        <Checkbox
          aria-label={`Select ${template.title}`}
          checked={!!selected}
          onCheckedChange={onToggleSelect}
          className="absolute top-3 right-3"
        />
      )}
    </div>
  )
}

type TemplateGalleryProps = {
  /** Start from a ready template (the caller opens the builder seeded from it). */
  onPick: (template: WorkflowTemplate) => void
  /** A deploy from the system screen finished, or its failures were accepted. The workflows now exist
   *  on the server, so the caller has nothing to seed and usually closes the gallery and refetches. */
  onDeployed?: () => void
  /** Whether to offer the way through to the system templates. A prop rather than a `useUser()` read
   *  inside, so the gallery stays a pure function of what it is handed and the caller owns the one
   *  permission question. Off by default: a caller that forgets to ask shows a practice's gallery,
   *  never ours. */
  showSystem?: boolean
  /** The workspace's locations and released toggles. A template none of them can run is padlocked,
   *  or left out when no practice system there could ever run it. */
  gating?: { locations: GateLocation[]; toggles: GateToggles }
}

/** The template gallery: the new-patient funnel playbooks, searchable and grouped by the outcome a
 *  practice is after. Lives on the workflows list (design language: template choice and trigger
 *  choice never share a flow, because a template already carries its trigger). Ready templates seed
 *  the builder; gated ones stay visible, since they sell the roadmap, but say honestly which engine
 *  capability they wait on.
 *
 *  Two screens. The gallery is the same for everybody, super or not. A super also gets a way through
 *  to the Action Center's own templates, which is a place you go rather than a group you scroll past. */
export function TemplateGallery({ onPick, onDeployed, showSystem, gating }: TemplateGalleryProps) {
  const [query, setQuery] = useState(``)
  const [category, setCategory] = useState<WorkflowTemplate[`category`] | null>(null)
  const [onSystemScreen, setOnSystemScreen] = useState(false)
  // Deploy mode on the system screen: cards become a multi-pick instead of a way into the builder,
  // and a footer asks which locations get them. Only the system screen offers this: the gallery's
  // templates carry blanks (a review link, a practice's own wording) that want a person in the
  // builder before they go live, and system ones do not.
  const [deployMode, setDeployMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const copy = useListCopy()
  const { system: systemTemplates, gallery: galleryTemplates } = useMemo(() => splitTemplates(copy), [copy])
  const locks = useMemo(
    () =>
      new Map(
        galleryTemplates.map((template) => [
          template.id,
          gating ? templateLock(template, gating.locations, gating.toggles) : undefined,
        ]),
      ),
    [galleryTemplates, gating],
  )
  const selectedTemplates = systemTemplates.filter((template) => selectedIds.has(template.id))
  const toggleSelected = (id: string) =>
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const setDeployModeAndForget = (on: boolean) => {
    setDeployMode(on)
    setSelectedIds(new Set())
  }
  const leaveSystemScreen = () => {
    setOnSystemScreen(false)
    setDeployModeAndForget(false)
  }

  // Escape backs out of the system screen one step at a time. The shared stack matters here: the
  // gallery can sit inside a Sheet with its own Escape dismiss, and a raw window listener would let
  // one keypress both back out the sub-screen and close the Sheet.
  useEscapeListener(leaveSystemScreen, onSystemScreen)

  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const termsKey = terms.join(` `)
  const groups = useMemo(() => {
    // Every term must appear somewhere, so "hygiene recall" narrows instead of widening.
    const matches = galleryTemplates.filter((template) => {
      const text = haystack(template)
      return (
        terms.every((term) => text.includes(term)) &&
        (!category || template.category === category) &&
        locks.get(template.id)?.state !== `hidden`
      )
    })
    return CATEGORY_ORDER.map((name) => ({
      name,
      // Ready first inside each group: what someone can use today outranks what we're promising.
      templates: matches
        .filter((template) => template.category === name)
        .sort((a, b) => Number(b.status === `ready`) - Number(a.status === `ready`)),
    })).filter((group) => group.templates.length > 0)
  }, [galleryTemplates, termsKey, category, locks])

  const matchCount = groups.reduce((total, group) => total + group.templates.length, 0)
  // Counts follow the search, not the catalogue. A chip reading "New patient enquiries 5" while a
  // query is narrowing to something else is a promise the click cannot keep; at zero it goes quiet
  // and stops being clickable.
  const searchMatches = useMemo(
    () => galleryTemplates.filter((template) => terms.every((term) => haystack(template).includes(term))),
    [galleryTemplates, termsKey],
  )
  const counts = CATEGORY_ORDER.map((name) => ({
    name,
    total: searchMatches.filter((template) => template.category === name).length,
  }))

  if (onSystemScreen) {
    // No search and no chips: three cards do not need finding, and the controls would say this
    // screen is a smaller version of the gallery when it is a different place.
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Button variant="subtle" size="icon-sm" onClick={leaveSystemScreen} aria-label="Back to templates">
            <ArrowLeftIcon size={14} weight="bold" className="size-3.5" />
          </Button>
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-strong">
            System templates
            <Crown width={14} height={14} className="text-amber-800" />
          </h3>
          <div className="ml-auto flex items-center gap-2">
            <label htmlFor="deploy-mode" className="cursor-pointer text-xs font-medium text-default">
              Deploy to locations
            </label>
            <Switch id="deploy-mode" checked={deployMode} onCheckedChange={setDeployModeAndForget} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {systemTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              // In deploy mode the whole card is the tick: a card that opened the builder on a
              // misclick would throw away the selection so far.
              onPick={() => (deployMode ? toggleSelected(template.id) : onPick(template))}
              selected={deployMode && selectedIds.has(template.id)}
              onToggleSelect={deployMode ? () => toggleSelected(template.id) : undefined}
            />
          ))}
        </div>
        {deployMode && (
          <DeployTemplatesFooter
            templates={selectedTemplates}
            toggles={gating?.toggles}
            // Only a super reaches this screen, and a super is exactly who may set the category.
            canSetCategory={!!showSystem}
            onDeployed={() => {
              setDeployModeAndForget(false)
              onDeployed?.()
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative flex min-w-56 flex-1 items-center">
          <MagnifyingGlassIcon size={14} weight="bold" className="pointer-events-none absolute left-3 text-minimal" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search templates"
            aria-label="Search templates"
            className="w-full rounded-lg border border-default bg-surface-subtle/60 py-1.5 pr-3 pl-8 text-sm text-default placeholder:text-minimal focus:border-strong focus:bg-surface focus:outline-none"
          />
        </label>
        <div className="flex flex-wrap gap-1.5">
          <Button
            variant="toggle"
            size="xs"
            aria-pressed={category === null}
            onClick={() => setCategory(null)}
            className={CHIP_CLASS}
          >
            All {searchMatches.length}
          </Button>
          {counts.map(({ name, total }) => (
            <Button
              key={name}
              variant="toggle"
              size="xs"
              aria-pressed={category === name}
              disabled={total === 0}
              onClick={() => setCategory(category === name ? null : name)}
              className={cn(CHIP_CLASS, total === 0 && `text-minimal`)}
            >
              {name} {total}
            </Button>
          ))}
        </div>
        {/* Only a super is ever handed `showSystem`, so this button is the whole permission surface:
            no button, no screen, and nothing of ours in the gallery either way. */}
        {showSystem && (
          <Button
            variant="white"
            size="xs"
            onClick={() => setOnSystemScreen(true)}
            className="ml-auto gap-1.5 border-amber-200 text-amber-900 hover:border-amber-300 hover:bg-amber-50"
          >
            <Crown width={13} height={13} className="size-[13px] text-amber-800" />
            System templates
          </Button>
        )}
      </div>

      {matchCount === 0 ? (
        <p className="py-6 text-center text-xs text-subtle">
          No template matches “{query.trim()}”. Try a word like recall, review or no-show.
        </p>
      ) : (
        groups.map((group) => (
          <section key={group.name}>
            <div className="mt-1 mb-2 flex items-baseline gap-2">
              <h3 className="text-[10px] font-semibold tracking-wide text-minimal uppercase">{group.name}</h3>
              <span className="text-[10px] text-minimal">{group.templates.length}</span>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {group.templates.map((template) => {
                const gate = locks.get(template.id)
                return (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onPick={() => onPick(template)}
                    lock={gate?.state === `locked` ? gate : undefined}
                    lockLocation={gating?.locations.length === 1 ? gating.locations[0] : undefined}
                  />
                )
              })}
            </div>
          </section>
        ))
      )}
    </div>
  )
}
